import { useEffect, useMemo, useRef, useState } from "react";
import { trpc } from "../../../lib/trpc.ts";
import type { OptionKind } from "./new-option-dialog.tsx";

/**
 * Clear the sub-category when the operator moves the product to a different
 * category — a child of "Shirts" is not a child of "Jackets", and leaving the
 * stale value in form state would save a pairing no picker ever offered.
 *
 * The first observed `category` seeds the ref and clears nothing. That matters
 * on the edit form, where the saved category arrives as the mount value: read
 * as a change it would wipe the saved sub-category before the operator touched
 * anything. Both forms mount with their real values and never `reset()`, so
 * every later change is genuinely the operator's.
 */
export function useSubCategoryReset(category: string, clear: () => void): void {
  const seen = useRef<string | null>(null);
  useEffect(() => {
    const previous = seen.current;
    seen.current = category;
    if (previous === null || previous === category) return;
    clear();
  }, [category, clear]);
}

export interface CatalogOptions {
  categories: { label: string; value: string }[];
  collections: { label: string; value: string }[];
  /**
   * Sub-categories saved under `category`, plus any added locally under it.
   * Empty for a blank parent — the child picker has nothing to offer until a
   * category is chosen, and says so through its placeholder.
   */
  subCategoriesFor: (category: string) => { label: string; value: string }[];
  /** True when the lookup failed — an empty list is then unknown, not empty. */
  failed: boolean;
  /** Add a value locally so it's selectable before the product is saved. */
  add: (kind: OptionKind, name: string, parentCategory?: string) => void;
  /** True once `name` is a real option — Radix shows the placeholder until then. */
  has: (kind: OptionKind, name: string, parentCategory?: string) => boolean;
  /** Placeholder that tells empty apart from broken. */
  placeholderFor: (kind: OptionKind, parentCategory?: string) => string;
}

const toOptions = (values: readonly string[]) => values.map((v) => ({ label: v, value: v }));

/** One locally-added sub-category, remembered against the parent it was filed under. */
export interface LocalSubCategory {
  category: string;
  name: string;
}

/**
 * The sub-categories offerable under `category` — server pairings, plus any
 * added locally under that same parent, plus this product's saved child.
 *
 * Pure and exported so the scoping rules are testable without a DOM: the hook
 * that wraps it is the only reason this logic would otherwise need one.
 *
 * A blank parent yields nothing. `saved` is only re-offered when it was saved
 * against this very parent — under a different one it isn't a member, and
 * offering it there would invent a pairing the catalogue never asserted.
 */
export function subCategoriesUnder(
  category: string,
  byCategory: Readonly<Record<string, readonly string[]>>,
  local: readonly LocalSubCategory[],
  saved?: { category?: string | undefined; subCategory?: string | undefined },
): { label: string; value: string }[] {
  const parent = category.trim();
  if (!parent) return [];
  const localNames = local.filter((s) => s.category === parent).map((s) => s.name);
  const savedName = saved?.category === parent ? saved?.subCategory : undefined;
  return toOptions(merge(byCategory[parent] ?? [], localNames, savedName));
}

/**
 * The option list `NewOptionDialog` checks a new name against.
 *
 * Sub-categories are scoped to the selected parent on purpose: the same name
 * under two categories is two different pairings, so rejecting it as a
 * duplicate would be wrong.
 */
export function optionsForDialog(
  kind: OptionKind,
  options: CatalogOptions,
  category: string,
): { label: string; value: string }[] {
  if (kind === "category") return options.categories;
  if (kind === "collection") return options.collections;
  return options.subCategoriesFor(category);
}

const merge = (server: readonly string[], local: readonly string[], selected?: string) => {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const v of [...server, ...local, ...(selected ? [selected] : [])]) {
    const t = v.trim();
    if (!t || seen.has(t.toLowerCase())) continue;
    seen.add(t.toLowerCase());
    out.push(t);
  }
  return out.sort((a, b) => a.localeCompare(b));
};

/**
 * Category, sub-category and collection choices for the product form.
 *
 * The taxonomy vocabulary is whatever the tenant's catalog already uses
 * (`storeCatalog.facets`) — neither level has a master table, so there is no
 * fixed list to hardcode. Values added through the dialog are held locally
 * until the product is saved, at which point the server starts returning them
 * and the local copy becomes redundant.
 *
 * Locally added sub-categories are keyed by their parent, not held flat. A flat
 * list would re-offer "Full sleeve" under every category once it was invented
 * under one, and would lose it entirely if the operator switched category away
 * and back before saving.
 *
 * Includes `current` values so an existing product's saved taxonomy still
 * appears in the pickers even if it was since archived out of the facets. The
 * saved sub-category is only re-offered under the category it was saved with —
 * under a different parent it isn't a member.
 */
export function useCatalogOptions(current?: {
  category?: string | undefined;
  subCategory?: string | undefined;
  collection?: string | undefined;
}): CatalogOptions {
  const facets = trpc.storeCatalog.facets.useQuery();
  const collectionList = trpc.storeCollections.list.useQuery({
    limit: 100,
    includeArchived: false,
  });
  const [added, setAdded] = useState<{
    categories: string[];
    subCategories: LocalSubCategory[];
    collections: string[];
  }>({ categories: [], subCategories: [], collections: [] });

  const categories = useMemo(
    () => toOptions(merge(facets.data?.categories ?? [], added.categories, current?.category)),
    [facets.data?.categories, added.categories, current?.category],
  );
  // Collections ARE a real table now (M6), so the picker is server-fed and its
  // values are ids rather than names — membership is a `store_collection_listings`
  // row, not a string on the listing. Archived collections are excluded, so a
  // product already in one keeps showing it via the `collectionName` fallback
  // on the form rather than being silently re-pointed.
  const collections = useMemo(
    () =>
      (collectionList.data?.items ?? []).map((c) => ({ label: c.name, value: c.collectionId })),
    [collectionList.data?.items],
  );

  const subCategoriesFor = useMemo(() => {
    const byCategory = facets.data?.subCategoriesByCategory ?? {};
    const saved = { category: current?.category, subCategory: current?.subCategory };
    return (category: string) => subCategoriesUnder(category, byCategory, added.subCategories, saved);
  }, [
    facets.data?.subCategoriesByCategory,
    added.subCategories,
    current?.category,
    current?.subCategory,
  ]);

  const listFor = (kind: OptionKind, parentCategory?: string) =>
    kind === "category"
      ? categories
      : kind === "subCategory"
        ? subCategoriesFor(parentCategory ?? "")
        : collections;

  return {
    categories,
    collections,
    subCategoriesFor,
    failed: facets.isError,
    placeholderFor: (kind, parentCategory) => {
      if (kind === "subCategory") {
        // Ordered deliberately: the missing parent is the operator's next
        // action, so it outranks "couldn't load" as the thing to say.
        if (!parentCategory?.trim()) return "Pick category first";
        if (facets.isError) return "Couldn't load sub-categories";
        if (facets.isLoading) return "Loading…";
        return subCategoriesFor(parentCategory).length
          ? "Select…"
          : "No sub-categories yet — add one";
      }
      const list = listFor(kind);
      if (list.length) return "Select…";
      if (kind === "collection") {
        if (collectionList.isError) return "Couldn't load collections";
        if (collectionList.isLoading) return "Loading…";
        return "No collections yet — add one in Collections";
      }
      // Don't dress a failed lookup up as an empty catalogue.
      if (facets.isError) return "Couldn't load categories";
      if (facets.isLoading) return "Loading…";
      return "No categories yet — add one";
    },
    has: (kind, name, parentCategory) =>
      listFor(kind, parentCategory).some((o) => o.value === name),
    add: (kind, name, parentCategory) =>
      setAdded((prev) => {
        if (kind === "category") return { ...prev, categories: [...prev.categories, name] };
        if (kind === "collection") return { ...prev, collections: [...prev.collections, name] };
        const parent = parentCategory?.trim();
        // Without a parent there is no pairing to record, and the value would
        // be invisible to every `subCategoriesFor` call. The form disables the
        // control until a category is picked; this is the belt to that braces.
        if (!parent) return prev;
        return { ...prev, subCategories: [...prev.subCategories, { category: parent, name }] };
      }),
  };
}
